// Minimal hash router + app shell. Views return a RenderedView; the shell draws
// the top app bar (back + title + optional action) and runs the view's mount().
import { icon } from './icons.js';
import { esc } from './dom.js';

export interface ViewAction {
  label: string;
  icon?: string;
  href?: string;
  onClick?: () => void;
}
export type TabId = 'quiz' | 'class' | 'students';
export interface RenderedView {
  title: string;
  subtitle?: string;
  back?: string;
  html: string;
  action?: ViewAction;
  /** fixed bottom action bar (e.g. Capture / Save) */
  bottom?: string;
  /** top-level tab: renders the persistent bottom nav (and no back button). */
  tab?: TabId;
  mount?: (root: HTMLElement) => void | Promise<void>;
  /** called when navigating away (e.g. stop the camera). */
  unmount?: () => void;
}

const TABS: Array<{ id: TabId; label: string; href: string; icon: () => string }> = [
  { id: 'quiz', label: 'Quiz', href: '/', icon: icon.doc },
  { id: 'class', label: 'Class', href: '/classes', icon: icon.users },
  { id: 'students', label: 'Students', href: '/students', icon: icon.user },
];

function tabBar(active: TabId): string {
  return `<nav class="tabbar" role="tablist">${TABS.map((t) => {
    const on = t.id === active;
    return `<a href="#${t.href}" class="${on ? 'on' : ''}" role="tab" aria-selected="${on}" aria-label="${t.label}">
      <span class="bubble-mark ${on ? 'filled' : ''}">${t.icon()}</span><span>${t.label}</span></a>`;
  }).join('')}</nav>`;
}
export type View = (params: Record<string, string>) => Promise<RenderedView>;

interface Route {
  re: RegExp;
  keys: string[];
  view: View;
}

const routes: Route[] = [];
export function route(pattern: string, view: View) {
  const keys: string[] = [];
  const re = new RegExp(
    '^' +
      pattern.replace(/:[^/]+/g, (m) => {
        keys.push(m.slice(1));
        return '([^/]+)';
      }) +
      '/?$',
  );
  routes.push({ re, keys, view });
}

export function navigate(hash: string) {
  if (location.hash === hash) render();
  else location.hash = hash;
}

function parse(): { path: string } {
  const h = location.hash.replace(/^#/, '');
  return { path: h || '/' };
}

let appEl: HTMLElement;
let token = 0;
let prevUnmount: (() => void) | undefined;

async function render() {
  const my = ++token;
  const { path } = parse();
  let matched: { view: View; params: Record<string, string> } | null = null;
  for (const r of routes) {
    const m = r.re.exec(path);
    if (m) {
      const params: Record<string, string> = {};
      r.keys.forEach((k, i) => (params[k] = decodeURIComponent(m[i + 1])));
      matched = { view: r.view, params };
      break;
    }
  }

  appEl.innerHTML = `<div class="appbar"><div class="spinner" style="margin:auto"></div></div>`;
  let rv: RenderedView;
  try {
    rv = matched
      ? await matched.view(matched.params)
      : { title: 'Not found', html: `<div class="view"><p class="empty">Page not found.</p></div>` };
  } catch (e: any) {
    rv = { title: 'Error', html: `<div class="view"><p class="empty danger-text">${esc(e?.message ?? e)}</p></div>` };
  }
  if (my !== token) return; // a newer navigation superseded this render
  prevUnmount?.();
  prevUnmount = rv.unmount;

  const backBtn = rv.back && !rv.tab
    ? `<a class="btn btn-icon btn-ghost" href="#${esc(rv.back)}" aria-label="Back" style="border:0;background:none">${icon.back()}</a>`
    : `<span style="width:8px"></span>`;
  const action = rv.action
    ? rv.action.href
      ? `<a class="btn btn-ghost btn-sm" href="#${esc(rv.action.href)}" data-action>${rv.action.icon ?? ''}${esc(rv.action.label)}</a>`
      : `<button class="btn btn-ghost btn-sm" data-action>${rv.action.icon ?? ''}${esc(rv.action.label)}</button>`
    : '';
  const titleBlock = `<h1>${esc(rv.title)}${rv.subtitle ? `<span class="sub">${esc(rv.subtitle)}</span>` : ''}</h1>`;

  appEl.classList.toggle('has-nav', !!rv.tab);
  appEl.innerHTML =
    `<header class="appbar">${backBtn}${titleBlock}${action}</header>` +
    rv.html +
    (rv.bottom ? `<div class="actionbar">${rv.bottom}</div>` : '') +
    (rv.tab ? tabBar(rv.tab) : '');

  if (rv.action?.onClick) {
    appEl.querySelector('[data-action]')?.addEventListener('click', (e) => {
      e.preventDefault();
      rv.action!.onClick!();
    });
  }
  await rv.mount?.(appEl);
  window.scrollTo(0, 0);
}

export function startRouter(mount: HTMLElement) {
  appEl = mount;
  window.addEventListener('hashchange', render);
  render();
}
